import React, { Component, useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react'
import type { ReactNode, ErrorInfo } from 'react'

import { YStack, Spinner, View, useThemeName } from 'tamagui'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useNavigate, useLocation } from 'react-router-dom'

import { useAuthContext } from '@data/useAuthContext'
import { useThemeContext } from '../ThemeContext'
import { Text } from '../Text'
import { EmojiIcon } from '../EmojiIcon/EmojiIcon'
import manShrugging from '../EmojiIcon/images/man-shrugging.png'
import { SearchResultRow } from './SearchResultRow'
import { SearchGroupItem } from './SearchGroup'
import { SearchGroup } from './SearchGroup'
import {
  SearchSuggestedActions,
  getTotalSuggestedActions,
} from './SearchSuggestedActions'
import { filterQuickActions, type QuickAction } from './searchQuickActions'
import { QuickActionRow } from './QuickActionRow'
import { useCloudflareInfo } from '@data/useCloudflareInfo'
import * as chrono from 'chrono-node'

import type { SearchOverlayQuery as SearchOverlayQueryType, SearchEntityType } from './__generated__/SearchOverlayQuery.graphql'
import type { SearchOverlayViewerQuery as SearchOverlayViewerQueryType } from './__generated__/SearchOverlayViewerQuery.graphql'
import type { UserPrimaryPhotoNew_user$key } from '../User/__generated__/UserPrimaryPhotoNew_user.graphql'
import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'

class SearchErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Error already logged by React
  }

  componentDidUpdate(prevProps: { children: ReactNode }) {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}

// Searchable entity types (Users omitted — not enough value yet)
const SEARCHABLE_TYPES: SearchEntityType[] = ['GUILD', 'EVENT', 'PRESENTATION']

const TYPE_PATTERNS: [RegExp, SearchEntityType][] = [
  [/\bevents?\b/, 'EVENT'],
  [/\bguilds?\b|\bnetworks?\b|\bcommunities?\b/, 'GUILD'],
  [/\bpresentations?\b|\btalks?\b/, 'PRESENTATION'],
]

const detectTypeFilter = (query: string): SearchEntityType[] => {
  const q = query.toLowerCase()
  const types = TYPE_PATTERNS.filter(([re]) => re.test(q)).map(([, t]) => t)
  if (types.length === 0) return SEARCHABLE_TYPES
  // When searching for events, also include guilds — guilds organize events
  // and surfacing them helps users find communities for future events.
  if (types.includes('EVENT') && !types.includes('GUILD')) {
    types.push('GUILD')
  }
  return types
}

// Strip type keywords and chrono-parsed temporal text from the query so the
// embedding model receives a cleaner, content-focused search string.
// Type filtering and date ranges are already handled by dedicated variables.
const cleanSearchQuery = (query: string): string => {
  let cleaned = query
  for (const [re] of TYPE_PATTERNS) {
    cleaned = cleaned.replace(new RegExp(re.source, 'i'), '')
  }
  // Remove temporal text that chrono-node matched
  const chronoResults = chrono.parse(query, new Date(), { forwardDate: true })
  for (const r of chronoResults) {
    cleaned = cleaned.replace(r.text, '')
  }
  // Collapse whitespace and trim
  cleaned = cleaned.replace(/\s+/g, ' ').trim()
  // Fall back to original query if stripping removed everything
  return cleaned || query
}

const detectDateRange = (query: string): { afterDate: string; beforeDate: string } | null => {
  const results = chrono.parse(query, new Date(), { forwardDate: true })
  if (results.length === 0) return null
  const result = results[0]

  // If chrono gave us an explicit range, use it
  if (result.end) {
    const start = result.start.date()
    const end = result.end.date()
    start.setHours(0, 0, 0, 0)
    end.setHours(23, 59, 0, 0)
    return { afterDate: start.toISOString(), beforeDate: end.toISOString() }
  }

  const comp = result.start
  const text = result.text.toLowerCase()

  // "this week" / "next week" — expand to 7-day range
  if (text.includes('week')) {
    const base = comp.date()
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0)
    if (text.includes('this')) {
      const today = new Date()
      start.setFullYear(today.getFullYear(), today.getMonth(), today.getDate())
      const dayOfWeek = today.getDay()
      const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
      const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysUntilSunday, 23, 59, 0, 0)
      return { afterDate: start.toISOString(), beforeDate: end.toISOString() }
    }
    const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000)
    end.setHours(23, 59, 0, 0)
    return { afterDate: start.toISOString(), beforeDate: end.toISOString() }
  }

  // Month granularity: certain month+year but not day → full month range
  if (comp.isCertain('month') && comp.isCertain('year') && !comp.isCertain('day')) {
    const month = comp.get('month')!
    const year = comp.get('year')!
    const start = new Date(year, month - 1, 1, 0, 0, 0, 0)
    const end = new Date(year, month, 0, 23, 59, 0, 0) // last day of month
    return { afterDate: start.toISOString(), beforeDate: end.toISOString() }
  }

  // Day granularity (default): snap to full day
  const date = result.start.date()
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 0, 0)
  return { afterDate: start.toISOString(), beforeDate: end.toISOString() }
}

const SearchOverlayResults = ({
  query,
  near,
  onResultCount,
  onClose,
  onResultHrefs,
  indexOffset,
}: {
  query: string
  near: { latitude: number; longitude: number } | null
  onResultCount: (count: number) => void
  onClose: () => void
  onResultHrefs: (hrefs: string[]) => void
  indexOffset: number
}) => {
  const types = detectTypeFilter(query)
  // Memoize so chrono-node's `new Date()` reference doesn't cause new objects each render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateRange = useMemo(() => detectDateRange(query), [query])
  // Strip type keywords and temporal text so the embedding model gets a cleaner query
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchQuery = useMemo(() => cleanSearchQuery(query), [query])

  const data = useLazyLoadQuery<SearchOverlayQueryType>(
    graphql`
      query SearchOverlayQuery(
        $query: String!
        $near: GeoPointInput
        $first: Int
        $types: [SearchEntityType!]
        $afterDate: Datetime
        $beforeDate: Datetime
      ) {
        search(query: $query, near: $near, first: $first, types: $types, afterDate: $afterDate, beforeDate: $beforeDate) {
          totalCount
          nodes {
            entityType
            entity {
              ... on Guild { slugId }
              ... on Event { slug }
              ... on Presentation { slug }
            }
            ...SearchResultRowNew_searchResult
          }
        }
      }
    `,
    {
      query: searchQuery,
      near,
      first: 7,
      types,
      afterDate: dateRange?.afterDate ?? null,
      beforeDate: dateRange?.beforeDate ?? null,
    },
    { fetchPolicy: 'network-only' }
  )

  const nodes = data.search.nodes
  const navigate = useNavigate()

  useEffect(() => {
    onResultCount(nodes.length)
  }, [nodes.length, onResultCount])

  // Report hrefs to parent for Enter key handling
  useEffect(() => {
    const hrefs = nodes.map((node) => getHref(node))
    onResultHrefs(hrefs)
  }, [nodes, onResultHrefs])

  if (nodes.length === 0) {
    return (
      <YStack padding='$4' alignItems='center' gap='$2'>
        <EmojiIcon size='$8' src={manShrugging} />
        <Text color='$color'>No results found</Text>
      </YStack>
    )
  }

  return (
    <>
      {nodes.map((node, index) => {
        const href = getHref(node)
        return (
          <SearchGroupItem
            key={`${node.entityType}-${index}`}
            index={indexOffset + index}
            onSelect={() => {
              onClose()
              navigate(href)
            }}
          >
            <SearchResultRow searchResult={node} />
          </SearchGroupItem>
        )
      })}
    </>
  )
}

// Derive navigation href from entity type + slug. These fields are requested
// directly in the query (not behind the fragment) so the overlay can build
// hrefs for keyboard navigation without reaching into masked fragment data.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getHref = (node: { entityType: string; entity: any }): string => {
  switch (node.entityType) {
    case 'GUILD':
      return `/${node.entity.slugId}`
    case 'EVENT':
      return `/events/${node.entity.slug}`
    case 'PRESENTATION':
      return `/presentations/${node.entity.slug}`
    default:
      return '/'
  }
}

// Small component that reads the viewer's slugId from Relay store (cache hit
// when DynamicIslandProfileButton has already fetched it) and reports it up.
const ViewerSlugIdReader = ({
  onViewer,
}: {
  onViewer: (slugId: string, userRef: UserPrimaryPhotoNew_user$key) => void
}) => {
  const data = useLazyLoadQuery<SearchOverlayViewerQueryType>(
    graphql`
      query SearchOverlayViewerQuery {
        viewer {
          slugId
          ...UserPrimaryPhotoNew_user @arguments(imageSize: 48, dpr: 3)
        }
      }
    `,
    {},
    { fetchPolicy: 'store-or-network' }
  )

  useEffect(() => {
    if (data.viewer?.slugId) onViewer(data.viewer.slugId, data.viewer)
  }, [data.viewer, onViewer])

  return null
}

export const SearchOverlay = ({ inputValue, onClose, onPopulateSearch }: { inputValue: string; onClose: () => void; onPopulateSearch: (text: string) => void }) => {
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [resultCount, setResultCount] = useState(0)
  const [viewerSlugId, setViewerSlugId] = useState<string | null>(null)
  const [viewerUserRef, setViewerUserRef] = useState<UserPrimaryPhotoNew_user$key | null>(null)
  const resultHrefsRef = useRef<string[]>([])
  const selectActiveRef = useRef<(() => void) | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { accessToken, setAuthTokens } = useAuthContext()
  const { toggleTheme, themeName: currentTheme } = useThemeContext()
  const cloudflareInfo = useCloudflareInfo()
  const themeName = useThemeName()
  const isDark = themeName === 'dark'

  const near =
    cloudflareInfo?.latitude && cloudflareInfo?.longitude
      ? { latitude: cloudflareInfo.latitude, longitude: cloudflareInfo.longitude }
      : null

  // Debounce search query
  useEffect(() => {
    if (inputValue.length < 2) {
      setDebouncedQuery('')
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(inputValue), 600)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Reset active index when query changes
  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery])

  // Compute matched quick actions instantly while typing (no debounce)
  const matchedQuickActions = useMemo(
    () => inputValue.trim() ? filterQuickActions(inputValue, !!accessToken) : [],
    [inputValue, accessToken]
  )

  const totalItems = debouncedQuery
    ? matchedQuickActions.length + resultCount
    : getTotalSuggestedActions(!!accessToken)

  const handleQuickAction = useCallback(
    async (action: QuickAction) => {
      onClose()

      if (action.type === 'toggle-theme') {
        toggleTheme()
        return
      }

      if (action.type === 'sign-out') {
        await setAuthTokens(undefined, undefined)
        navigate('/')
        return
      }

      const staticRoutes: Record<string, string> = {
        home: '/',
        'create-event': '/events/new',
        'create-presentation': '/presentations/new',
        'create-guild': '/guilds/new',
      }

      if (action.id === 'sign-in') {
        navigate('/sign-in', { state: { backgroundLocation: location } })
        return
      }

      if (action.id === 'sign-up') {
        navigate('/sign-up', { state: { backgroundLocation: location } })
        return
      }

      const route = action.id === 'profile' ? `/${viewerSlugId}` :
                    action.id === 'settings' ? `/${viewerSlugId}/settings` :
                    staticRoutes[action.id] || '/'

      // Create actions redirect through sign-up if not signed in
      if (action.id.startsWith('create-') && !accessToken) {
        navigate('/sign-up', {
          state: { backgroundLocation: location, authRedirectPath: route },
        })
        return
      }

      navigate(route)
    },
    [navigate, onClose, accessToken, location, viewerSlugId, setAuthTokens, toggleTheme]
  )

  // Handle Enter — delegate to the SearchGroup's registered onSelect callbacks
  // so animated typewriter rows use their current text, not the static label.
  const handleSelectActive = useCallback(() => {
    selectActiveRef.current?.()
  }, [])

  // Global keyboard listener using capture phase on document.
  // The search input lives inside the Dynamic Island popover (top-layer),
  // and React/Tamagui stops propagation before events reach window.
  // Capture phase fires before any component handler can swallow the event.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((prev) => Math.min(prev + 1, totalItems - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          handleSelectActive()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }
    document.addEventListener('keydown', handler, { capture: true })
    return () => document.removeEventListener('keydown', handler, { capture: true })
  }, [totalItems, handleSelectActive, onClose])

  const onResultHrefs = useCallback((hrefs: string[]) => {
    resultHrefsRef.current = hrefs
  }, [])

  const handleViewer = useCallback((slugId: string, userRef: UserPrimaryPhotoNew_user$key) => {
    setViewerSlugId(slugId)
    setViewerUserRef(userRef)
  }, [])

  const panelBg = isDark ? 'rgba(30,30,30,0.75)' : 'rgba(255,255,255,0.75)'

  return (
    <>
      {/* Fetch viewer slugId for profile/settings routes */}
      {accessToken ? (
        <Suspense fallback={null}>
          <ViewerSlugIdReader onViewer={handleViewer} />
        </Suspense>
      ) : null}

      {/* Backdrop */}
      <View
        position='fixed'
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor='rgba(0,0,0,0.4)'
        style={{ backdropFilter: 'blur(4px)', zIndex: 299_999 } as React.CSSProperties}
        onPress={onClose}
      />

      {/* Results panel */}
      <YStack
        position='fixed'
        top={72}
        left='50%'
        width='100%'
        maxWidth={600}
        overflow='hidden'
        borderRadius={24}
        style={{
          transform: 'translateX(-50%)',
          zIndex: 300_000,
          backgroundColor: panelBg,
          backdropFilter: 'blur(8px) saturate(250%)',
          WebkitBackdropFilter: 'blur(8px) saturate(250%)',
          boxShadow:
            'rgba(0, 0, 0, 0.08) 0px 8px 8px -2px, rgba(0, 0, 0, 0.05) 0px 15px 25px 10px',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'rgba(128, 128, 128, 0.08)',
        } as React.CSSProperties}
      >
        {/* Results or suggested actions */}
        <YStack>
          {debouncedQuery ? (
            <SearchErrorBoundary
              fallback={
                <YStack padding='$4' alignItems='center' gap='$2'>
                  <EmojiIcon size='$8' src={manShrugging} />
                  <Text color='$color'>Something went wrong</Text>
                </YStack>
              }
            >
              <Suspense
                fallback={
                  <YStack padding='$4' alignItems='center'>
                    <Spinner />
                  </YStack>
                }
              >
                <SearchGroup
                  activeIndex={activeIndex}
                  onActiveIndexChange={setActiveIndex}
                  selectActiveRef={selectActiveRef}
                >
                  {matchedQuickActions.map((action, index) => (
                    <QuickActionRow
                      key={action.id}
                      action={action}
                      index={index}
                      onSelect={() => handleQuickAction(action)}
                      viewerUser={action.id === 'profile' ? viewerUserRef : null}
                      currentTheme={action.id === 'toggle-theme' ? currentTheme : undefined}
                    />
                  ))}
                  <SearchOverlayResults
                    query={debouncedQuery}
                    near={near}
                    onResultCount={setResultCount}
                    onClose={onClose}
                    onResultHrefs={onResultHrefs}
                    indexOffset={matchedQuickActions.length}
                  />
                </SearchGroup>
              </Suspense>
            </SearchErrorBoundary>
          ) : (
            <SearchGroup
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
              selectActiveRef={selectActiveRef}
            >
              <SearchSuggestedActions
                isSignedIn={!!accessToken}
                onSelectQuickAction={handleQuickAction}
                onPopulateSearch={onPopulateSearch}
                viewerUser={viewerUserRef}
                currentTheme={currentTheme}
              />
            </SearchGroup>
          )}
        </YStack>
      </YStack>
    </>
  )
}
